import React, { ReactElement } from 'react'
import { ICheckoutSession } from '../state/Radom'
import LinkExternal from '../components/LinkExternal'
import TimeText from '../components/TimeText'
import { PAY_BASE_URL, RADOM_COLORS } from '../util/Constants'
import Table from '../components/Table'
import { StatusBadge } from '../views/CheckoutSessions'
import { ExternalLinkText } from '../views/Events'
import PaymentMethodDisplay from './PaymentMethodDisplay'
import {
  ItemDataThumbnail,
  ProductThumbnail, PaymentDetailsSection,
  PaymentAssetIcon
} from './PaymentDetails'
import RefundDetails from './RefundDetails'
import { camelCase, startCase } from 'lodash'
import { Section, SubTitle, SectionContent, SectionItem } from './Shared'
import { trimZeros } from '../util/Util'
import Checkmark from '../icons/Checkmark'
import Close from '../icons/Close'
import { InfoIcon as Info } from '../icons/Info'
import { format } from 'date-fns'
import { RotateArrows } from '../icons/RotateArrows'
import styled from 'styled-components'
import Tooltip from './Tooltip'

interface CheckoutSessionDetailsProps {
  checkoutSession: ICheckoutSession
  netPayment: number
}

const TimelineContainer = styled.div`
  margin-top: 40px;
  border-top: 1px solid ${RADOM_COLORS.GRAY_DARK};
  padding-top: 30px;
`

const TimelineItems = styled.div`
  max-width: 600px;
`

const TimelineItem = styled.div<{ isLast: boolean }>`
  display: flex;
  gap: 20px;
  position: relative;
  padding-bottom: ${props => (props.isLast ? '0' : '25px')};
`

const VerticalLine = styled.div`
  position: absolute;
  left: 11px;
  top: 26px;
  bottom: 0;
  width: 2px;
  background: #EAECEF;
`

const IconWrapper = styled.div<{ isLatest: boolean, color: string }>`
  z-index: 1;
  background: ${props => (props.isLatest ? props.color : 'white')};
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  border: 2px solid ${props => (props.isLatest ? props.color : '#EAECEF')};
  box-shadow: ${props => (props.isLatest ? `0 0 12px ${props.color}40` : 'none')};
`

const ContentWrapper = styled.div`
  flex-grow: 1;
  margin-top: 2px;
`

const StatusHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`

const StatusTitle = styled.div<{ isLatest: boolean }>`
  font-weight: 600;
  font-size: 14px;
  color: ${props => (props.isLatest ? RADOM_COLORS.BLACK : '#64748B')};
  display: flex;
  align-items: center;
  gap: 5px;
`

const StatusReason = styled.div`
  color: #666;
  font-size: 12px;
  margin-top: 4px;
`

const StatusDate = styled.div`
  color: #666;
  font-size: 12px;
`

const CheckoutSessionDetails = ({ checkoutSession, netPayment }: CheckoutSessionDetailsProps): ReactElement => {
  const Field = ({
    label,
    children
  }: {
    label: string
    children: React.ReactNode
  }): ReactElement => (
    <div style={{ display: 'flex', gap: 8, fontSize: 12 }}>
      <div style={{ minWidth: 140, color: '#666' }}>{label}</div>
      <div style={{ fontWeight: 500 }}>{children}</div>
    </div>
  )

  const formatTimestamp = (date: string): string => {
    try {
      return format(new Date(date), 'dd/MM/yyyy HH:mm')
    } catch (e: unknown) {
      console.error(e)
      return date
    }
  }

  const getStatusColor = (status: string): string => {
    switch (status) {
    case 'paid':
    case 'refunded':
    case 'partially_refunded':
      return RADOM_COLORS.RADOM_BLUE
    case 'failed':
    case 'refused':
    case 'refund_failed':
    case 'in_dispute':
    case 'disputed':
    case 'canceled':
      return RADOM_COLORS.RED900
    case 'processing':
    case 'finalizing':
    case 'waiting_for_payment':
    case 'waiting_for_bank_confirmation':
    case 'in_refund':
      return RADOM_COLORS.GRAY_DARKEST
    default:
      return RADOM_COLORS.GRAY_DARK
    }
  }
  const getStepIcon = (status: string, size: number, isActive: boolean): ReactElement => {
    const color = isActive ? 'white' : RADOM_COLORS.GRAY_DARK
    switch (status) {
    case 'paid':
    case 'refunded':
    case 'partially_refunded':
      return <Checkmark style={{ width: size, height: size, stroke: color }} />
    case 'failed':
    case 'refused':
    case 'refund_failed':
    case 'in_dispute':
    case 'canceled':
    case 'disputed':
      return <Close style={{ width: size, height: size, stroke: color }} />
    case 'processing':
    case 'finalizing':
    case 'waiting_for_payment':
    case 'waiting_for_bank_confirmation':
    case 'in_refund':
      return <RotateArrows style={{ width: size, height: size, stroke: color }} />
    default:
      return <Info style={{ width: size, height: size, color }} />
    }
  }
  return <>
    <Section>
      <SubTitle style={{ fontSize: 18 }}>
        Checkout session details
      </SubTitle>

      <SectionContent>
        <SectionItem>
          <SubTitle>ID</SubTitle>
          <ExternalLinkText href={`${PAY_BASE_URL}/checkout/${checkoutSession.id}`} target='_blank'>
            {checkoutSession.id}
            <LinkExternal stroke={RADOM_COLORS.BLACK} width={13} />
          </ExternalLinkText>
        </SectionItem>

        <SectionItem>
          <SubTitle>Status</SubTitle>
          <StatusBadge status={checkoutSession.sessionStatus}>{checkoutSession.sessionStatus}</StatusBadge>
        </SectionItem>

        <SectionItem>
          <SubTitle>Created at</SubTitle>
          <div>
            <TimeText textColor={RADOM_COLORS.BLACK} date={new Date(checkoutSession.createdAt)} />
          </div>
        </SectionItem>

        {
          checkoutSession.items && checkoutSession.items.length > 0 &&
          <SectionItem>
            <SubTitle>Line items</SubTitle>
            <div style={{ fontSize: 14 }}>
              {checkoutSession.items.map((item, i) => <ItemDataThumbnail key={i} p={item} />)}
            </div>
          </SectionItem>
        }
        {
          checkoutSession.products && checkoutSession.products.length > 0 &&
          <SectionItem>
            <SubTitle>Products</SubTitle>
            <div style={{ fontSize: 14 }}>
              {checkoutSession.products.map((item, i) =>
                <ProductThumbnail key={i} p={item} />
              )}
            </div>
          </SectionItem>
        }

      </SectionContent>
      {
        checkoutSession && checkoutSession.metadata.length > 0 &&
        <div style={{ fontSize: 14 }}>
          <SubTitle style={{ fontSize: 14, paddingBottom: 10 }}>Metadata</SubTitle>
          <Table
            disableHoverStyle
            headers={[
              <span key="productType">Key</span>,
              <span key="quantity">Value</span>
            ]}
            items={
              checkoutSession.metadata.map(p => [
                <span key="type">{p.key}</span>,
                <span key="quantity">{p.value}</span>
              ])
            }
            isLoading={false}
          />
        </div>
      }
    </Section>

    {
      checkoutSession.openBankingPaymentData &&
          <Section>
            <SubTitle style={{ fontSize: 18 }}>
                Open Banking Payment Data
            </SubTitle>

            <SectionContent>
              <SectionItem>
                <SubTitle style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  Payment Status
                  <Tooltip
                    infoIconStyle={{ width: 14, opacity: 0.5 }}
                    style={{
                      maxWidth: '380px',
                      backgroundColor: '#fff',
                      borderRadius: '12px',
                      padding: '16px 20px',
                      boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                      border: '1px solid rgba(0, 0, 0, 0.05)'
                    }}
                    message={(
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                        {[
                          { s: 'processing', t: 'The customer started the payment but hasn\'t executed it yet.' },
                          { s: 'waiting_for_bank_confirmation', t: 'The customer confirms the payment was authorized, but bank has not yet confirmed the authorization status.' },
                          { s: 'finalizing', t: 'Payment received, finalizing booking verification. Typically completes in minutes, may take up to 48 hours if additional verification is required.' },
                          { s: 'paid', t: 'The money was received.' },
                          { s: 'canceled', t: 'The order process has been canceled. No money arrived, or we\'ll refund it automatically.' },
                          { s: 'failed', t: 'Money has not arrived in collection account within expected timeframe and will not arrive anymore.' },
                          { s: 'in_refund', t: 'A refund has been initialized and the money is currently on the way to the customer\'s bank account.' },
                          { s: 'refunded', t: 'The money has been refunded to the customer.' },
                          { s: 'partially_refunded', t: 'The money has been partially refunded to the customer.' },
                          { s: 'refund_failed', t: 'The refund was rejected.' }
                        ].map((item, idx, arr) => (
                          <div key={item.s} style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 3,
                            borderBottom: idx === arr.length - 1 ? 'none' : '1px solid #f1f5f9',
                            paddingBottom: idx === arr.length - 1 ? 0 : 10
                          }}>
                            <div style={{
                              fontWeight: 600,
                              color: '#0f172a',
                              fontSize: '10px',
                              textTransform: 'uppercase',
                              letterSpacing: '0.05em',
                              fontFamily: 'Inter, system-ui, sans-serif'
                            }}>
                              {startCase(item.s)}
                            </div>
                            <div style={{
                              color: '#64748b',
                              fontSize: '12px',
                              fontWeight: 400,
                              lineHeight: '1.5',
                              fontFamily: 'Inter, system-ui, sans-serif'
                            }}>
                              {item.t}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  />
                </SubTitle>
                <StatusBadge status={camelCase(checkoutSession.openBankingPaymentData.status)}>
                  {camelCase(checkoutSession.openBankingPaymentData.status).replace(/([a-z])([A-Z])/g, '$1 $2')}</StatusBadge>
              </SectionItem>
              <SectionItem>
                <SubTitle>Scheme</SubTitle>
                <div>{checkoutSession.openBankingPaymentData.paymentScheme}</div>
              </SectionItem>
              <SectionItem>
                <SubTitle>Instant Payment Scheme</SubTitle>
                <div>{checkoutSession.openBankingPaymentData.instantPaymentScheme ? 'Yes' : 'No'}</div>
              </SectionItem>
              {checkoutSession.openBankingPaymentData.payerFinancialAddress && (
                <SectionItem>
                  <SubTitle>Payer Financial Address</SubTitle>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {/* IBAN */}
                    {checkoutSession.openBankingPaymentData.payerFinancialAddress.iban && (
                      <>
                        <Field label="IBAN">
                          {checkoutSession.openBankingPaymentData.payerFinancialAddress.iban.iban}
                        </Field>

                        {checkoutSession.openBankingPaymentData.payerFinancialAddress.iban.accountHolderName && (
                          <Field label="Account Holder">
                            {checkoutSession.openBankingPaymentData.payerFinancialAddress.iban.accountHolderName}
                          </Field>
                        )}

                        {checkoutSession.openBankingPaymentData.payerFinancialAddress.iban.bic && (
                          <Field label="BIC">
                            {checkoutSession.openBankingPaymentData.payerFinancialAddress.iban.bic}
                          </Field>
                        )}
                      </>
                    )}

                    {/* Other formats (future-proof) */}
                    {checkoutSession.openBankingPaymentData.payerFinancialAddress.sortCode && (
                      <Field label="Sort Code">
                        {checkoutSession.openBankingPaymentData.payerFinancialAddress.sortCode}
                      </Field>
                    )}

                    {checkoutSession.openBankingPaymentData.payerFinancialAddress.bankCode && (
                      <Field label="Bank Code">
                        {checkoutSession.openBankingPaymentData.payerFinancialAddress.bankCode}
                      </Field>
                    )}

                    {checkoutSession.openBankingPaymentData.payerFinancialAddress.bban && (
                      <Field label="BBAN">
                        {checkoutSession.openBankingPaymentData.payerFinancialAddress.bban}
                      </Field>
                    )}
                  </div>
                </SectionItem>
              )}
              {
                checkoutSession.openBankingPaymentData.shopper &&
                <SectionItem>
                  <SubTitle>Customer</SubTitle>
                  {checkoutSession.openBankingPaymentData.shopper.email &&
                    <div>Email: {checkoutSession.openBankingPaymentData.shopper.email}</div>}
                  {checkoutSession.openBankingPaymentData.shopper.phoneNumber &&
                    <div>Phone Number: {checkoutSession.openBankingPaymentData.shopper.phoneNumber}</div>}
                </SectionItem>
              }
            </SectionContent>
            {
              checkoutSession.openBankingPaymentData.statusHistoryList &&
              checkoutSession.openBankingPaymentData.statusHistoryList.length > 0 &&
                            <TimelineContainer>
                              <SubTitle style={{ marginBottom: 20 }}>Payment History</SubTitle>
                              <TimelineItems>
                                {checkoutSession.openBankingPaymentData?.statusHistoryList
                                  .slice().reverse().map((s, i) => {
                                    const isLatest = i === 0
                                    const isLast = i ===
                                     checkoutSession.openBankingPaymentData.statusHistoryList.length - 1
                                    return (
                                      <TimelineItem key={`${s.currentStatus}-${s.createdAt}`} isLast={isLast}>
                                        {!isLast && <VerticalLine />}

                                        <IconWrapper isLatest={isLatest} color={getStatusColor(s.currentStatus)}>
                                          {getStepIcon(s.currentStatus, 12, isLatest)}
                                        </IconWrapper>

                                        <ContentWrapper>
                                          <StatusHeader>

                                            <div>

                                              <StatusTitle isLatest={isLatest}>
                                                {startCase(s.currentStatus)}

                                              </StatusTitle>
                                              <StatusReason>
                                                {startCase(s.reason.toLowerCase())}
                                              </StatusReason>

                                            </div>

                                            <StatusDate>
                                              {formatTimestamp(s.updatedAt)}
                                            </StatusDate>
                                          </StatusHeader>
                                        </ContentWrapper>
                                      </TimelineItem>
                                    )
                                  })}
                              </TimelineItems>
                            </TimelineContainer>}
          </Section>
    }

    {
      checkoutSession.payment?.managed &&
      <PaymentDetailsSection payment={{ managed: checkoutSession.payment?.managed }} netPayment={netPayment} />
    }

    {
      checkoutSession?.payment?.managedTokenDelegatePayment &&
      <PaymentDetailsSection payment={{ managed: checkoutSession.payment.managedTokenDelegatePayment }}
        netPayment={netPayment} />
    }
    {
      checkoutSession?.payment?.openBanking &&
      <Section>
        <SubTitle style={{ fontSize: 18 }}>
          Payment details
        </SubTitle>

        <SectionContent>

          <SectionItem>
            <SubTitle>ID</SubTitle>
            <ExternalLinkText href={`/payment/${checkoutSession?.payment?.openBanking.paymentEventId}`} target='_blank'>
              {checkoutSession?.payment.openBanking.paymentEventId}
              <LinkExternal stroke={RADOM_COLORS.BLACK} width={13} />
            </ExternalLinkText>
          </SectionItem>

          <SectionItem>
            <SubTitle>Asset</SubTitle>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <PaymentMethodDisplay
                style={{ marginRight: 5 }}
                method={{
                  network: checkoutSession.payment.openBanking.network,
                  token: checkoutSession.payment.openBanking.token
                }}
                excludeName
                disableTooltip={true} />
              {checkoutSession.payment.openBanking.token}
            </div>
          </SectionItem>

          <SectionItem>
            <SubTitle>
              <span>Onramp Status</span>
            </SubTitle>
            <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              {
                checkoutSession?.payment.openBanking.onrampStatus == 'FundsReceived'
                  ? <StatusBadge status="success">Success</StatusBadge>
                  : <StatusBadge status={checkoutSession?.payment.openBanking.onrampStatus ? camelCase(checkoutSession?.payment.openBanking.onrampStatus) : 'Pending'}>
                    {checkoutSession?.payment.openBanking.onrampStatus ? camelCase(checkoutSession?.payment.openBanking.onrampStatus).replace(/([a-z])([A-Z])/g, '$1 $2') : 'Pending'}
                  </StatusBadge>
              }

            </div>
          </SectionItem>

          <SectionItem>
            <SubTitle>
              <span>Total Payment</span>
            </SubTitle>
            <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <PaymentAssetIcon payment={{
                network: checkoutSession.payment.openBanking.network,
                token: checkoutSession?.payment?.openBanking.token
              }} />
              <span>{checkoutSession?.payment?.openBanking.amount}</span>
            </div>
          </SectionItem>
          {
            (checkoutSession?.payment.openBanking?.onrampStatus === 'Complete' ||
              checkoutSession?.payment?.openBanking.onrampGrossAmount) && <>
              <SectionItem>
                <SubTitle>
                  <span>Onramp Gross Amount</span>
                </SubTitle>
                <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                  <PaymentAssetIcon payment={{
                    network: checkoutSession.payment.openBanking.onrampNetwork || 'Ethereum',
                    token: checkoutSession?.payment?.openBanking.onrampToken
                  }} />
                  <span>{trimZeros(checkoutSession?.payment?.openBanking.onrampGrossAmount ?? '0')}</span>
                </div>
              </SectionItem>

              <SectionItem>
                <SubTitle>
                  <span>Onramp Net Amount</span>
                </SubTitle>
                <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                  <PaymentAssetIcon payment={{
                    network: checkoutSession.payment.openBanking.onrampNetwork || 'Ethereum',
                    token: checkoutSession?.payment?.openBanking.onrampToken
                  }} />
                  <span>{trimZeros(checkoutSession?.payment?.openBanking.onrampNetAmount ?? '0')}</span>
                </div>
              </SectionItem>

              <SectionItem>
                <SubTitle>
                  <span>Onramp Fee</span>
                </SubTitle>
                <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                  <PaymentAssetIcon payment={{
                    network: checkoutSession.payment.openBanking.onrampNetwork || 'Ethereum',
                    token: checkoutSession?.payment?.openBanking.onrampToken
                  }} />
                  <span>{trimZeros(checkoutSession?.payment?.openBanking.onrampFee ?? '0')}</span>
                </div>
              </SectionItem>
            </>
          }

        </SectionContent>
      </Section>
    }

    {
      checkoutSession.refunds.length > 0 &&
      <RefundDetails refunds={checkoutSession.refunds} />
    }
  </>
}

export default CheckoutSessionDetails
